import React, { useState, useRef } from 'react';
import Tool, { type ToolButtonComponent } from './Tool';
import ToolbarMenu from '../toolbars/ToolbarMenu';
import { ApplicationView, useController } from '@aha-app/mvc';
import ToolButton from '../toolbars/ToolButton';
import classNames from 'classnames';
import styles from '../styles.module.css';
import layoutHorizontal from '../assets/icons/layoutHorizontal.svg';
import layoutVertical from '../assets/icons/layoutVertical.svg';
import layoutAutoGapSVG from '../assets/icons/layoutAutoGap.svg';
import { svgToIcon } from '../utils/svg';
import ToolbarRow from '../toolbars/ToolbarRow';
import WhiteboardController from '../controllers/WhiteboardController';
import { type Instance as TippyInstance } from 'tippy.js/index';
import ToolbarHDivider from '../toolbars/ToolbarHDivider';
import Tippy, { useSingleton } from '@tippyjs/react';
import { TOOLTIP_DELAY_GENERAL } from '../constants';
import { type SingletonObject } from '../types';
import type LayoutBehaviors from '../objects/LayoutBehaviors';
import { tippyPopoverPlugin } from '../utils/tippyPopoverPlugin';

export const ToggleButton: React.FC<{
  controller: WhiteboardController;
  selection: fabric.Object;
  currentValue: string;
  setCurrentValue: (string) => void;
  buttonProperty: string;
  buttonValue: string;
  buttonSvg: string;
  name: string;
  singleton: SingletonObject;
  disabled?: boolean;
}> = ApplicationView(
  ({
    controller,
    selection,
    currentValue,
    setCurrentValue,
    buttonProperty,
    buttonValue,
    buttonSvg,
    name,
    singleton,
    disabled = false,
  }) => {
    return (
      <ToolButton
        onClick={() => {
          setCurrentValue(buttonValue);
          selection.setOnObjects({ [buttonProperty]: buttonValue });
          Tool.toolActionComplete({ controller });
        }}
        className={classNames({
          [styles.toolButtonActive]: currentValue === buttonValue,
        })}
        disabled={disabled}
        toolbarSingleton={singleton}
        name={name}
      >
        {svgToIcon(buttonSvg)}
      </ToolButton>
    );
  }
);

const AutoGapButton: React.FC<{
  controller: WhiteboardController;
  selection: fabric.Object;
  currentLayoutDirection: string;
  singleton: SingletonObject;
  name: string;
}> = ApplicationView(
  ({ controller, selection, currentLayoutDirection, singleton, name }) => {
    const [layoutGap, setLayoutGap] = useState(
      selection.firstObject().get('layoutGap')
    );

    return (
      <ToolButton
        key='layoutDirection'
        name={name}
        toolbarSingleton={singleton}
        active={layoutGap === 'auto'}
        onClick={() => {
          const newValue = layoutGap === 'auto' ? 10 : 'auto';
          const newProperties: any = { layoutGap: newValue };
          setLayoutGap(newValue);
          if (newValue === 'auto') {
            if (currentLayoutDirection === 'horizontal') {
              newProperties.layoutAlignHorizontal = 'center';
            } else {
              newProperties.layoutAlignVertical = 'center';
            }
          }
          selection.setOnObjects(newProperties);
          Tool.toolActionComplete({ controller });
        }}
      >
        {svgToIcon(layoutAutoGapSVG)}
      </ToolButton>
    );
  }
);

interface PopupProps {
  selection: LayoutBehaviors & fabric.Object;
  layoutDirection: LayoutBehaviors['layoutDirection'];
  setLayoutDirection: (string: LayoutBehaviors['layoutDirection']) => void;
}
const Popup: React.FC<PopupProps> = ApplicationView(
  ({ selection, layoutDirection, setLayoutDirection }) => {
    const controller = useController(WhiteboardController);
    const [tippySingletonSource, singleton] = useSingleton();
    const showAutoGapButton = selection.showAutoGapButton();
    const colCount = showAutoGapButton ? 3 : 2;

    return (
      <ToolbarMenu columns={colCount} columnWidth={42}>
        <Tippy
          singleton={tippySingletonSource}
          placement='top'
          delay={TOOLTIP_DELAY_GENERAL}
          plugins={[tippyPopoverPlugin]}
        />
        <ToolbarRow>
          <ToggleButton
            name='Arrange shapes horizontally'
            singleton={singleton}
            controller={controller}
            selection={selection}
            currentValue={layoutDirection}
            setCurrentValue={setLayoutDirection}
            buttonProperty='layoutDirection'
            buttonValue='horizontal'
            buttonSvg={layoutHorizontal}
          />
          <ToggleButton
            name='Arrange shapes vertically'
            singleton={singleton}
            controller={controller}
            selection={selection}
            currentValue={layoutDirection}
            setCurrentValue={setLayoutDirection}
            buttonProperty='layoutDirection'
            buttonValue='vertical'
            buttonSvg={layoutVertical}
          />
          {showAutoGapButton && (
            <>
              <ToolbarHDivider />
              <AutoGapButton
                name='Fill container'
                singleton={singleton}
                controller={controller}
                selection={selection}
                currentLayoutDirection={layoutDirection}
              />
            </>
          )}
        </ToolbarRow>
      </ToolbarMenu>
    );
  }
);

// Configure all of the layout settings.
class ToolLayoutOrientation extends Tool {
  static toolName = 'Orientation';

  static buttonComponent() {
    const Button: ToolButtonComponent = ApplicationView(
      ({ selection, tool, controller }) => {
        const tippyRef = useRef<TippyInstance>();

        const [layoutDirection, setLayoutDirection] = useState(
          selection.firstObject().get('layoutDirection')
        );

        const setCurrentLayoutDirection = layoutDirection => {
          setLayoutDirection(layoutDirection);
          tool.toolActionComplete({ controller });
        };

        const icon =
          layoutDirection === 'horizontal'
            ? svgToIcon(layoutHorizontal)
            : svgToIcon(layoutVertical);

        // TODO rerenderToolbar closes this popup but is needed to update the other
        // icons in the toolbar

        return (
          <ToolButton
            popup={
              <Popup
                selection={selection as LayoutBehaviors & fabric.Object}
                layoutDirection={layoutDirection}
                setLayoutDirection={setCurrentLayoutDirection}
              />
            }
            tippyRef={tippyRef}
            id='layoutSettings'
            tool={tool}
          >
            {icon}
          </ToolButton>
        );
      }
    );

    return Button;
  }
}

export default ToolLayoutOrientation;
